// client/components/Login/TwoFASetup.jsx
import React, { useEffect, useRef, useState } from "react";
import api from "../../constants/api";
import { showSuccess, showError, showInfo, promise, handleError } from "../../utils/toast.js";
import { useNavigate, Link } from "react-router-dom";
import { ScanFace, QrCode, Loader2, AlertCircle, RefreshCcw, Copy, Check, Info } from "lucide-react";
import { registerPasskey, isPasskeyAvailable } from "../../constants/passkeys";
import AppButton from "../ui/AppButton.jsx";
import { AuthPageLayout, AuthScreenHeadline, authOtpCellClassName } from "./AuthVisualShell.jsx";
import { successButtonLoadingClassName } from "../../design/tokens.js";
import { cn } from "../../utils/themeClasses.js";
import { Input } from "../forms/shared/FormAtoms.jsx";
export default function TwoFASetup() {
    const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [otpauth, setOtpauth] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [code, setCode] = useState(Array(6).fill(""));
  const [cooldown, setCooldown] = useState(0);
  const [showSecret, setShowSecret] = useState(false);
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const nav = useNavigate();
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const copyTimerRef = useRef(null);

  const startCooldown = (s = 8) => {
    if (!mountedRef.current) return;
    setCooldown(s);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  const normalizeCode = () => code.join("").replace(/\s|-/g, "");
  const loadQR = async () => {
    if (cooldown > 0) return;
    if (mountedRef.current) {
      setLoading(true);
      setErr("");
    }
    try {
      const {
        data
      } = await api.post("/2fa/setup", {});
      if (!mountedRef.current) return;
      if (data?.erfolg && (data.qr || data.secret || data.otpauth)) {
        setQr(data.qr || "");
        setSecret(data.secret || "");
        setOtpauth(data.otpauth || "");
      } else {
        setErr(data?.msg || "Konnte QR-Code nicht erstellen.");
      }
    } catch (e) {
      if (!mountedRef.current) return;
      const s = e?.response?.status;
      setErr(s === 401 ? "Nicht eingeloggt." : s === 403 ? "Zugriff verweigert." : "Serverfehler beim Erstellen des QR-Codes.");
    } finally {
      if (mountedRef.current) setLoading(false);
      startCooldown(8);
    }
  };
  useEffect(() => {
    mountedRef.current = true;
    loadQR();
    (async () => {
      const ok = await isPasskeyAvailable();
      if (mountedRef.current) setPasskeyReady(ok);
    })();
    return () => {
      mountedRef.current = false;
      clearInterval(timerRef.current);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);
  const enable = async e => {
    e?.preventDefault?.();
    const token = normalizeCode();
    if (token.length !== 6) return showError("Bitte 6-stelligen Code eingeben");
    if (mountedRef.current) setEnabling(true);
    try {
      const {
        data
      } = await promise(api.post("/2fa/enable", {
        token
      }), {
        loading: "Aktiviere 2FA…",
        success: "2FA aktiviert.",
        error: "srv.error"
      });
      if (data?.erfolg) {
        return nav("/first-password");
      }
      showError(data?.msg || "Code ungültig");
    } catch (e2) {
      handleError(e2, "srv.error");
    } finally {
      if (mountedRef.current) setEnabling(false);
    }
  };
  const doRegisterPasskey = async () => {
    if (!passkeyReady || passkeyBusy) return;
    if (mountedRef.current) setPasskeyBusy(true);
    try {
      const r = await registerPasskey();
      if (r?.erfolg) showSuccess("Passkey eingerichtet (Face/Touch ID)!");else {
        const m = r?.msg || r?.error || "Passkey-Registrierung fehlgeschlagen";
        showError(m);
      }
    } catch (e) {
      // Häufig: NotAllowedError (Abbruch), InvalidStateError (dupliziert)
      const raw = e?.response?.data?.msg || e?.message || "Registrierung abgebrochen/fehlgeschlagen";
      const friendly = /NotAllowedError/i.test(raw) ? "Abgebrochen." : /InvalidStateError|already/i.test(raw) ? "Schon registriert." : raw;
      showError(friendly);
    } finally {
      if (mountedRef.current) setPasskeyBusy(false);
    }
  };
  const pasteOtp = val => {
    const digits = String(val).replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const next = Array(6).fill("");
    for (let i = 0; i < digits.length; i++) next[i] = digits[i];
    setCode(next);
    if (digits.length === 6) enable(); // Auto-submit bei vollständigem Paste
  };
  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      if (!mountedRef.current) return;
      setCopiedField(field);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        if (mountedRef.current) setCopiedField("");
      }, 1200);
    } catch {
      showInfo("Markiere & kopiere manuell (Strg/Cmd+C).");
    }
  };
  return (
    <AuthPageLayout docTitle="2FA einrichten • WireTrack" contentClassName="space-y-3.5 sm:space-y-6">
          <AuthScreenHeadline
            variant="hero"
            kicker="Sicherheit"
            title="Zwei-Faktor-Authentifizierung"
            description="Scanne den QR-Code und gib einen gültigen 6-stelligen Code ein, um TOTP zu aktivieren."
          />

          {(err || loading) && (
            <div
              className={`rounded-2xl px-4 py-3 text-sm flex items-start gap-2 border ${
                err ? "bg-red-500/10 text-red-100 border-red-500/25" : "bg-blue-500/10 text-blue-100 border-blue-400/20"
              }`}
              role="status"
            >
              {err ? <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <Loader2 className="w-4 h-4 mt-0.5 shrink-0 animate-spin" />}
              <p>{err || "QR wird geladen…"}</p>
            </div>
          )}

          {/* TOTP Bereich */}
          <div className="space-y-3">
            <div className={`flex items-center gap-2 text-xs ${"text-blue-100/80"}`}>
              <QrCode className="w-4 h-4" />
              <span>TOTP (Google/Microsoft Authenticator)</span>
            </div>

            {/* QR + Refresh */}
            {!loading && !err && <>
                <div className="flex justify-center">
                  {qr ? (
                    <img src={qr} alt="2FA QR" className="border rounded-xl p-2 max-w-[260px] border-white/10 bg-white/[0.04]" />
                  ) : (
                    <div className="text-sm text-blue-200/80">Kein QR empfangen.</div>
                  )}
                </div>

                <div className="flex items-center justify-center gap-2">
                  <AppButton type="button" onClick={loadQR} disabled={cooldown > 0} className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg transition-all ${"bg-white/10 hover:bg-white/20 disabled:opacity-50"}`} variant="secondary" size="sm">
                    <RefreshCcw className={`w-4 h-4 ${cooldown ? "" : "animate-spin-slow"}`} />
                    {cooldown ? `Neu laden in ${cooldown}s` : "QR neu laden"}
                  </AppButton>

                  <AppButton type="button" onClick={() => setShowSecret(s => !s)} className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg transition-all ${"bg-white/10 hover:bg-white/20"}`} variant="secondary" size="sm">
                    {showSecret ? "QR verbergen" : "Kann nicht scannen?"}
                  </AppButton>
                </div>

                {showSecret && <div className="mt-2 space-y-2 rounded-xl border border-white/10 bg-white/[0.04] p-3">
                    <p className={`text-xs flex items-center gap-2 ${"text-blue-200/70"}`}>
                      <Info className="w-3.5 h-3.5" />
                      Du kannst den Secret-Key auch manuell eingeben oder den otpauth-Link nutzen.
                    </p>

                    {!!secret && <CopyRow label="Secret" value={secret} copied={copiedField === "secret"} onCopy={() => copy(secret, "secret")} />}
                    {!!otpauth && <CopyRow label="otpauth://" value={otpauth} copied={copiedField === "otpauth"} onCopy={() => copy(otpauth, "otpauth")} small />}
                  </div>}

                <ol className={`text-xs md:text-sm mb-1 list-decimal pl-5 space-y-1 ${"text-blue-100/80"}`}>
                  <li>Öffne Google/Microsoft Authenticator oder Authy.</li>
                  <li>„Konto hinzufügen“ → QR-Code scannen (oder Secret manuell eingeben).</li>
                  <li>6-stelligen Code unten eingeben und bestätigen.</li>
                </ol>

                <form onSubmit={enable} className="space-y-3">
                  <OtpBoxes value={code} onChange={setCode} onPaste={pasteOtp} />
                  <AppButton type="submit" disabled={enabling} className={cn("w-full py-3 rounded-2xl min-h-[48px] font-semibold justify-center gap-2", enabling && successButtonLoadingClassName)} variant="success" size="sm">
                    Aktivieren
                  </AppButton>
                  <p className={`text-[11px] ${"text-white/50"}`}>
                    Codes rotieren alle ~30s. Falls „ungültig“, kurz warten und erneut versuchen.
                  </p>
                </form>
              </>}

            {/* Fehler + Retry */}
            {!loading && err && <div className="space-y-2">
                <AppButton type="button" onClick={loadQR} disabled={cooldown > 0} className={`text-sm font-medium hover:underline disabled:opacity-50 transition-colors ${"text-blue-300 hover:text-blue-200"}`} variant="ghost" size="sm">
                  Erneut versuchen {cooldown ? `(${cooldown}s)` : ""}
                </AppButton>
              </div>}
          </div>

          {/* Passkey (Face/Touch ID) */}
          <div className={`pt-2 border-t space-y-2 ${"border-white/10"}`}>
            <div className={`flex items-center gap-2 text-xs ${"text-blue-100/80"}`}>
              <ScanFace className="w-4 h-4" />
              <span>Passkey (Face ID / Touch ID) einrichten</span>
            </div>
            <AppButton type="button" disabled={!passkeyReady || passkeyBusy} onClick={doRegisterPasskey} className="w-full py-3 rounded-2xl text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-all bg-white/[0.06] border-white/10 hover:bg-white/[0.12] text-white/90 border" variant="secondary" size="sm">
              {passkeyBusy && <Loader2 className="w-4 h-4 animate-spin" />}
              {passkeyReady ? "Passkey jetzt einrichten" : "Passkeys hier nicht verfügbar"}
            </AppButton>
            <p className={`text-[11px] ${"text-white/50"}`}>
              Nach dem Einrichten kannst du dich mit „Face ID / Passkey anmelden“ einloggen oder im 2FA-Schritt statt Code die Face ID verwenden.
            </p>
          </div>

          <div className="flex justify-center pt-1">
            <Link to="/login" className="text-xs text-blue-300/80 hover:text-blue-200 underline-offset-2 hover:underline transition-colors">
              Zur Anmeldung
            </Link>
          </div>
    </AuthPageLayout>
  );
}

/* ---------- Subcomponents ---------- */

function OtpBoxes({
  value,
  onChange,
  onPaste
}) {
  const inputsRef = useRef([]);
  const setDigit = (idx, v) => {
    const d = v.replace(/\D/g, "");
    const next = [...value];
    next[idx] = d.slice(-1) || "";
    onChange(next);
    if (d && idx < 5) inputsRef.current[idx + 1]?.focus();
  };
  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !value[idx] && idx > 0) {
      const prev = idx - 1;
      inputsRef.current[prev]?.focus();
      const next = [...value];
      next[prev] = "";
      onChange(next);
      e.preventDefault();
    }
    if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) inputsRef.current[idx + 1]?.focus();
  };
  return <div className="grid grid-cols-6 gap-2" onPaste={e => {
    e.preventDefault();
    onPaste?.(e.clipboardData.getData("text") || "");
  }}>
      {value.map((v, i) => (
        <Input
          key={i}
          ref={el => (inputsRef.current[i] = el)}
          value={v}
          onChange={e => setDigit(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          className={authOtpCellClassName}
          aria-label={`Code Ziffer ${i + 1}`}
        />
      ))}
    </div>;
}
function CopyRow({
  label,
  value,
  onCopy,
  copied,
  small
}) {
  return <div className="flex items-center justify-between gap-2">
      <div className={`truncate ${small ? "text-[11px]" : "text-xs"} ${"text-blue-100/80"}`}>
        <span className="opacity-70 mr-2">{label}:</span>
        <span className="font-mono">{value}</span>
      </div>
      <AppButton type="button" onClick={onCopy} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-all ${"bg-white/10 hover:bg-white/20"}`} variant="secondary" size="sm">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Kopiert" : "Kopieren"}
      </AppButton>
    </div>;
}